function arrayEquals(a: any[], b: any[]): boolean {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
}

/**
 * Takes two objects and returns a new object based on `obj1`,
 * excluding any keys with the same value as in `obj2`.
 *
 * @param obj1 - The first object to filter.
 * @param obj2 - The second object to compare against.
 * @returns A new object with filtered key-value pairs.
 * @throws Error if `obj1` and `obj2` do not have the same keys.
 */

function filterMatchingPairs<T extends Record<string, any>, U extends Record<string, any>>(
  obj1: T,
  obj2: U
): Partial<T> {
  // The keys should be sorted the same way so two objects with the same keys in different order match
  const keys1 = Object.keys(obj1).slice().sort();
  const keys2 = Object.keys(obj2).slice().sort();
  if (!arrayEquals(keys1, keys2)) {
    throw new Error(`obj1 and obj2 must have the same keys - obj1: ${keys1} - obj2: ${keys2}`);
  }

  // Convert obj1 to key-value pair array
  const entries = Object.entries(obj1);

  // Filter out any keys that have the same value as the matching key in obj2
  const filteredEntries = entries.filter(([key, value]) => value !== obj2[key]);

  // Create an object from the filtered array
  return Object.fromEntries(filteredEntries) as Partial<T>;
}

export default filterMatchingPairs;
